import React, { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Copy, ClipboardCheck, Pencil, Trash2, Plus, Search } from "lucide-react";

const emptyDraft = { id: null, title: "", body: "", category: "" };

export default function Prompts() {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [copiedCat, setCopiedCat] = useState(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/prompts");
      setPrompts(r.data || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Agrupa por categoría respetando el orden en que aparecieron
  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? prompts.filter((p) =>
          (p.title || "").toLowerCase().includes(q) ||
          (p.body || "").toLowerCase().includes(q) ||
          (p.category || "").toLowerCase().includes(q))
      : prompts;
    const map = new Map();
    for (const p of filtered) {
      const k = p.category || "Sin categoría";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(p);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0], "es"));
  }, [prompts, query]);

  const allCategories = useMemo(() => {
    const set = new Set(prompts.map((p) => p.category || "Sin categoría"));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "es"));
  }, [prompts]);

  const openNew = (presetCat = "") => {
    setDraft({ ...emptyDraft, category: presetCat });
    setEditorOpen(true);
  };
  const openEdit = (p) => {
    setDraft({ id: p.id, title: p.title || "", body: p.body || "", category: p.category || "" });
    setEditorOpen(true);
  };

  const copyText = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); }
      catch { toast.error("No se pudo copiar"); return; }
      document.body.removeChild(ta);
    }
    setCopiedCat(key);
    setTimeout(() => setCopiedCat((cur) => (cur === key ? null : cur)), 1800);
    toast.success("Copiado al portapapeles");
  };

  const copyCategory = (category, list) => {
    const header = category.toUpperCase();
    const body = list
      .map((p, i) => `${i + 1}. ${(p.body?.trim() || p.title || "").trim()}`)
      .join("\n");
    const text = `${header}\n${"—".repeat(Math.min(header.length, 40))}\n${body}\n`;
    copyText(text, `cat:${category}`);
  };

  const onSave = async () => {
    const title = draft.title.trim();
    if (!title) return toast.error("El título es obligatorio");
    setSaving(true);
    try {
      const payload = {
        title,
        body: draft.body || "",
        category: (draft.category || "Sin categoría").trim() || "Sin categoría",
      };
      if (draft.id) {
        const r = await api.patch(`/prompts/${draft.id}`, payload);
        setPrompts((cur) => cur.map((p) => (p.id === draft.id ? r.data : p)));
        toast.success("Prompt actualizado");
      } else {
        const r = await api.post("/prompts", payload);
        setPrompts((cur) => [...cur, r.data]);
        toast.success("Prompt creado");
      }
      setEditorOpen(false);
      setDraft(emptyDraft);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/prompts/${confirmDelete.id}`);
      setPrompts((cur) => cur.filter((p) => p.id !== confirmDelete.id));
      toast.success("Prompt eliminado");
      setConfirmDelete(null);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="p-4 sm:p-8 max-w-6xl mx-auto pb-24" data-testid="prompts-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="pe-label">PORTAPAPELES TÉCNICO</div>
          <h1 className="text-3xl sm:text-4xl font-black text-[color:var(--pe-primary)] mt-1">
            {prompts.length} Prompts · Ingeniería eléctrica
          </h1>
          <p className="text-sm text-slate-500 mt-2 max-w-2xl">
            Cada categoría se puede copiar completa con un solo botón. Pasa el mouse sobre un ítem
            para editarlo o eliminarlo; usa <b>+ Nuevo prompt</b> para agregar uno.
          </p>
        </div>
        <Button onClick={() => openNew()} className="pe-btn-primary text-white h-11 px-5"
                data-testid="btn-new-prompt">
          <Plus size={16} className="mr-1.5" /> Nuevo prompt
        </Button>
      </div>

      <div className="mt-6 pe-card p-3 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <Input placeholder="Buscar por texto o categoría…" value={query}
                 onChange={(e) => setQuery(e.target.value)}
                 className="pl-9 h-11" data-testid="prompt-search-input" />
        </div>
      </div>

      {loading ? (
        <div className="mt-8 text-slate-500">Cargando prompts…</div>
      ) : grouped.length === 0 ? (
        <div className="mt-8 pe-card p-10 text-center text-slate-500">
          {prompts.length === 0
            ? "Aún no hay prompts. Crea el primero para tu equipo."
            : "Sin resultados con ese filtro."}
        </div>
      ) : (
        <div className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {grouped.map(([category, list]) => {
            const catKey = `cat:${category}`;
            const isCopied = copiedCat === catKey;
            return (
              <section key={category}
                       data-testid={`prompt-group-${category}`}
                       className="pe-card p-5 flex flex-col">
                <header className="flex items-start justify-between gap-2 mb-3">
                  <h2 className="text-[13px] font-black tracking-[0.12em] uppercase text-[#0A1118] leading-snug">
                    {category}
                  </h2>
                  <button onClick={() => copyCategory(category, list)}
                          title={`Copiar los ${list.length} prompts de ${category}`}
                          data-testid={`btn-copy-category-${category}`}
                          className={`shrink-0 inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1.5 rounded-md transition-colors ${
                            isCopied
                              ? "bg-emerald-600 text-white"
                              : "bg-[color:var(--pe-primary)] text-white hover:bg-[#004a7a]"
                          }`}>
                    {isCopied ? (
                      <><ClipboardCheck size={13} /> Copiado</>
                    ) : (
                      <><Copy size={13} /> Copiar {list.length}</>
                    )}
                  </button>
                </header>

                <ol className="space-y-1.5 flex-1">
                  {list.map((p, idx) => (
                    <li key={p.id}
                        data-testid={`prompt-card-${p.id}`}
                        className="group flex items-start gap-2 text-sm text-slate-700 hover:bg-slate-50 rounded-md px-1.5 py-1 -mx-1.5 transition-colors">
                      <span className="pe-numeric text-[11px] text-slate-400 font-bold w-5 pt-0.5 shrink-0 text-right">
                        {idx + 1}
                      </span>
                      <span className="flex-1 leading-snug break-words" data-testid={`prompt-title-${p.id}`}>
                        {p.title}
                      </span>
                      <span className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex items-center gap-0.5 shrink-0">
                        <button onClick={() => openEdit(p)}
                                title="Editar"
                                data-testid={`btn-edit-prompt-${p.id}`}
                                className="p-1 rounded hover:bg-white text-slate-500 hover:text-[color:var(--pe-primary)]">
                          <Pencil size={13} />
                        </button>
                        <button onClick={() => copyText(p.body?.trim() || p.title || "", `p:${p.id}`)}
                                title="Copiar solo este"
                                data-testid={`btn-copy-prompt-${p.id}`}
                                className={`p-1 rounded hover:bg-white ${
                                  copiedCat === `p:${p.id}`
                                    ? "text-emerald-600"
                                    : "text-slate-500 hover:text-[color:var(--pe-primary)]"
                                }`}>
                          {copiedCat === `p:${p.id}` ? <ClipboardCheck size={13} /> : <Copy size={13} />}
                        </button>
                        <button onClick={() => setConfirmDelete(p)}
                                title="Eliminar"
                                data-testid={`btn-delete-prompt-${p.id}`}
                                className="p-1 rounded hover:bg-white text-red-400 hover:text-red-600">
                          <Trash2 size={13} />
                        </button>
                      </span>
                    </li>
                  ))}
                </ol>

                <button onClick={() => openNew(category)}
                        data-testid={`btn-add-to-cat-${category}`}
                        className="mt-3 text-xs font-bold text-[color:var(--pe-accent)] hover:underline self-start">
                  + Agregar a {category.split(" ")[0].toLowerCase()}…
                </button>
              </section>
            );
          })}
        </div>
      )}

      {/* Editor Modal */}
      <Dialog open={editorOpen} onOpenChange={(o) => { setEditorOpen(o); if (!o) setDraft(emptyDraft); }}>
        <DialogContent className="max-w-lg" data-testid="prompt-editor-dialog">
          <DialogHeader>
            <DialogTitle className="text-[color:var(--pe-primary)] font-black">
              {draft.id ? "Editar prompt" : "Nuevo prompt"}
            </DialogTitle>
            <DialogDescription>
              El botón <b>Copiar</b> por categoría concatena el cuerpo de cada prompt numerado.
              Si el cuerpo queda vacío, se copia el título.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="pe-label">Título *</Label>
              <Input value={draft.title}
                     onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                     className="h-11 mt-1" autoFocus
                     data-testid="prompt-form-title" />
            </div>
            <div>
              <Label className="pe-label">Categoría</Label>
              <Input value={draft.category} placeholder="Ej: Cálculo cargas residenciales"
                     onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))}
                     className="h-11 mt-1" list="prompt-categories"
                     data-testid="prompt-form-category" />
              <datalist id="prompt-categories">
                {allCategories.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div>
              <Label className="pe-label">Cuerpo (texto que se copiará)</Label>
              <Textarea value={draft.body}
                        onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))}
                        rows={8} className="mt-1"
                        placeholder="Escribe aquí el texto completo del prompt/plantilla…"
                        data-testid="prompt-form-body" />
            </div>
          </div>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setEditorOpen(false)}
                    data-testid="prompt-form-cancel">Cancelar</Button>
            <Button onClick={onSave} disabled={saving}
                    className="pe-btn-primary text-white"
                    data-testid="prompt-form-save">
              {saving ? "Guardando…" : draft.id ? "Guardar cambios" : "Crear prompt"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <DialogContent className="max-w-sm" data-testid="prompt-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600 font-black">Eliminar prompt</DialogTitle>
            <DialogDescription>
              ¿Seguro que quieres borrar “{confirmDelete?.title}”? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}
                    data-testid="prompt-delete-cancel">Cancelar</Button>
            <Button onClick={onDelete}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="prompt-delete-confirm">
              Sí, eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
